/* eslint-disable @typescript-eslint/no-empty-function */
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { InfoTag } from 'shared/ui/InfoTag';
import { ReactComponent as BillIcon } from 'shared/assets/icons/newIcons/PaidDaysIcon.svg';
import { ReactComponent as SquareIcon } from 'shared/assets/icons/newIcons/SizeIcon.svg';
import { ReactComponent as AccessIcon } from 'shared/assets/icons/newIcons/AccessIcon.svg';
import { Button } from 'shared/ui/Button';
import { CachedBox, LockStatusMarker, useWatchLockState } from 'entities/Box';
import { Reservation } from 'entities/Reservation';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import dayjs from 'dayjs';
import { cancelReservationModalActions } from 'features/CancelReservationModal';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { BookingDocumentsNames } from 'app/appState/model/types';
import { takeUpReservationModalActions } from 'widgets/TakeUpReservationModal';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { OpenBox } from 'features/OpenBox';
import { ContractWithBookingInfo, UnitType } from 'entities/Contract';
import { reservationDetailDrawerActions } from 'widgets/ReservationDetailsDrawer';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { Modal } from 'shared/ui/Modal';
import { WarehouseMap } from 'widgets/SelectBoxSizeForm/ui/WarehouseMap';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface BoxCardProps {
  box: Nullable<CachedBox>;
  type: 'reservation' | 'contract';
  onOpenContractDetails: (contractId: string) => void;
  reservation?: Nullable<Reservation>;
  warehouseId?: string;
  contractsData?: ContractWithBookingInfo[];
}

export const BoxCard: FC<BoxCardProps> = memo((props) => {
  const { box, type, reservation, warehouseId, contractsData, onOpenContractDetails } = props;
  const isReservation = type === 'reservation';

  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const globalSettings = useAppSelector(getGlobalSettings);

  const boxUsageRulesDocument = useMemo(
    () => globalSettings?.bookingDocuments.find(({ docName }) => docName === BookingDocumentsNames.RULES),
    [globalSettings?.bookingDocuments],
  );

  const { t } = useAppTranslation(['contracts', 'common', 'booking']);

  const { isDesktop } = useScreenBreakpoints();

  const lockStatus = useWatchLockState(box, warehouseId);

  const isExternalBox = box?.timeToOpenLockMS != null;
  const [isMapOpen, setMapOpen] = useState(false);

  const contractWithBookingInfo = useMemo(
    () => contractsData?.find((contractData) => contractData.contractId === box?.contract?.contractId),
    [box?.contract?.contractId, contractsData],
  );

  const openContractDetails = useCallback((): void => {
    contractWithBookingInfo && onOpenContractDetails(contractWithBookingInfo.contractId);
  }, [contractWithBookingInfo, onOpenContractDetails]);

  const openCancelReservationModal = useCallback((): void => {
    if (reservation) {
      dispatch(cancelReservationModalActions.openCancelReservationModal(reservation));
    }
  }, [dispatch, reservation]);

  const openTakeUpReservationModal = useCallback((): void => {
    if (reservation) {
      dispatch(takeUpReservationModalActions.openTakeUpReservationModal(reservation));
    }
  }, [dispatch, reservation]);

  const renderContractInvoicingInfo = useCallback(() => {
    if (contractWithBookingInfo?.isContractPaid) {
      return contractWithBookingInfo.nextInvoiceAmount ? (
        <div className="flex flex-col desktop:items-end">
          <div className="text-primaryLight text-sm desktop:text-lg font-tektur">
            {t('Next payment')}:{' '}
            <span className="text-accent font-semibold text-sm desktop:text-lg font-tektur">
              {getClientDateFormat(dayjs(contractWithBookingInfo.nextInvoiceDueDate))}
            </span>
          </div>
          <div className="text-primaryLight text-sm desktop:text-lg self-start font-tektur">
            {t('Total')}:{' '}
            <span className="text-accent font-semibold text-sm desktop:text-lg font-tektur">
              {formatPrice(contractWithBookingInfo.nextInvoiceAmount)} {currencySymbol}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col desktop:items-end">
          <div className="text-primaryLight text-sm desktop:text-lg font-tektur">
            {t('Calculated to')}:{' '}
            <span className="text-accent font-semibold text-sm desktop:text-lg font-tektur">
              {getClientDateFormat(dayjs(contractWithBookingInfo.chargedTo))}
            </span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col desktop:items-end">
          <div className="text-error text-sm desktop:text-lg self-start font-tektur">
            {t('Total debt')}:{' '}
            <span className="text-error text-sm desktop:text-lg font-semibold font-tektur">
              {formatPrice(contractWithBookingInfo?.contractDebt)} {currencySymbol}
            </span>
          </div>
        </div>
      );
    }
  }, [
    contractWithBookingInfo?.chargedTo,
    contractWithBookingInfo?.contractDebt,
    contractWithBookingInfo?.isContractPaid,
    contractWithBookingInfo?.nextInvoiceAmount,
    contractWithBookingInfo?.nextInvoiceDueDate,
    currencySymbol,
    t,
  ]);

  const minRentalPeriod = dayjs(reservation?.endRentDate).diff(reservation?.startRentDate, reservation?.invoiceFrequencyType);
  const minRentalPeriodValue = `${minRentalPeriod} ${t(
    minRentalPeriod === 1 ? `${reservation?.invoiceFrequencyType}` : `${reservation?.invoiceFrequencyType}s`,
  )}`;

  const openReservationDetails = useCallback((): void => {
    if (reservation) {
      dispatch(reservationDetailDrawerActions.setOpenReservationDetails(reservation.reservationId));
    }
  }, [dispatch, reservation]);

  const openMapModal = useCallback((): void => {
    setMapOpen(true);
  }, []);

  const closeMapModal = useCallback((): void => {
    setMapOpen(false);
  }, []);

  const openBoxUsageRules = useCallback((): void => {
    if (boxUsageRulesDocument?.link) {
      window.open(boxUsageRulesDocument.link, '_blank', 'noopener,noreferrer');
    }
  }, [boxUsageRulesDocument?.link]);

  const boxUsageRulesLink = boxUsageRulesDocument ? (
    <Button
      theme="clear"
      fontSize="small"
      size={isDesktop ? 'inline' : undefined}
      className={isDesktop ? 'underline !w-fit mb-2' : 'underline !w-fit'}
      onClick={openBoxUsageRules}
    >
      {t(boxUsageRulesDocument.docName, { ns: 'booking' })}
    </Button>
  ) : null;

  const desktopVersion = (
    <div className="flex p-6 bg-white rounded-xl">
      <div className="flex-1 flex gap-4">
        <img className="rounded-lg max-w-[160px] max-h-[160px] mr-4" src={box?.sizeCode?.imageUrl} alt="box image" />
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-2xl flex items-center gap-4 font-tektur">
            {box?.name}
            <div className="mb-2">{!isReservation && <LockStatusMarker lockStatus={lockStatus} isExternalBox={isExternalBox} />}</div>
          </div>

          <div className="flex flex-col gap-1">
            <Button theme="clear" fontSize="small" className="underline !w-fit mb-2" size="inline" onClick={openMapModal}>
              {t('Show on map')}
            </Button>
            <div className="flex items-center text-grey text-sm">
              <SquareIcon className="mr-2 text-accent w-[14px] h-[14px]" />
              {formatDecimal(box?.square)} {getSizeCodeTypeString(box?.sizeCode?.sizeCodeType)}
            </div>
            {!isReservation ? (
              <>
                {contractWithBookingInfo?.isContractPaid ? (
                  <div className="flex items-center text-grey text-sm">
                    <BillIcon className="mr-2 text-accent w-[15px] h-[15px]" />
                    {t('Paid days')}: {contractWithBookingInfo.paidDays}
                  </div>
                ) : (
                  <div className="flex items-center text-error text-sm">
                    <BillIcon className="mr-2 text-error w-[15px] h-[15px]" />
                    {t('Paid days')}: 0
                  </div>
                )}
                {contractWithBookingInfo?.isContractPaid ? (
                  <div className="flex items-center text-grey text-sm">
                    <AccessIcon className="mr-2 text-accent w-[15px] h-[15px]" />
                    {t('Access is allowed')}
                  </div>
                ) : (
                  <div className="flex items-center text-error text-sm">
                    <AccessIcon className="mr-2 text-error w-[15px] h-[15px]" />
                    {t('Access is denied')}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-x-1.5 desktop:space-x-0 desktop:flex desktop:items-center desktop:gap-1">
                  <span className="text-accent text-sm">{t('Start rent date')}:</span>
                  <span className="text-sm text-primaryLight">{getClientDateFormat(reservation?.startRentDate)}</span>
                </div>
                <div className="space-x-1.5 desktop:space-x-0 desktop:flex desktop:items-center desktop:gap-1">
                  <span className="text-accent text-sm">{t('Min rental period')}:</span>
                  <span className="text-sm text-primaryLight">{minRentalPeriodValue}</span>
                </div>
              </>
            )}
            {boxUsageRulesLink}
          </div>
          {box?.floor && (
            <span className="text-primaryLight text-lg px-5 py-1 rounded-3xl border border-grey w-fit mt-2">
              {getLocalizedString(box.floor.name)}
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-between gap-5">
        {reservation && isReservation ? (
          <div className="flex flex-col justify-between h-full">
            <div className="self-end">
              <div onClick={openReservationDetails} className="text-lg text-primaryLight font-tektur">
                {t('Reservation')}:
                <span className="pl-1 text-accent font-semibold cursor-pointer underline font-tektur">
                  #{reservation.reservationNumber}
                </span>
              </div>
              <div className="text-error font-semibold text-lg font-tektur">
                {t('Expires')}:{' '}
                <span className="text-black font-semibold font-tektur">{getClientDateFormat(reservation.expirationDate)}</span>
              </div>
            </div>

            <div className="flex gap-1.5">
              <Button theme="outlined" onClick={openCancelReservationModal} containerClassName="w-full">
                {t('Cancel reservation')}
              </Button>
              {box?.unitType !== UnitType.OFFICE && (
                <Button onClick={openTakeUpReservationModal} containerClassName="w-full">
                  {t('Enter into contract')}
                </Button>
              )}
              {box?.unitType === UnitType.OFFICE && (
                <InfoTag className="w-full">{t('Administrator should contact you to provide instructions for rent')}</InfoTag>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="self-end text-primaryLight text-sm desktop:text-lg font-tektur">
              {t('Contract')}:
              <span className="pl-1 text-accent font-semibold cursor-pointer underline font-tektur" onClick={openContractDetails}>
                #{contractWithBookingInfo?.contractNumber}
              </span>
              {renderContractInvoicingInfo()}
            </div>

            <div className="flex gap-1.5">
              <Button theme="secondary" onClick={openContractDetails} containerClassName="w-full" className="!text-accent !border-accent">
                {t('Contract details')}
              </Button>
              <OpenBox
                box={box}
                action={
                  <Button iconPosition="prev" containerClassName="w-full">
                    {t('Open')}
                  </Button>
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  const mobileVersion = (
    <div className="flex flex-col p-4 bg-white rounded-xl">
      <div className="flex-1 flex gap-6">
        <img className="rounded-lg max-w-[130px] max-h-[130px]" src={box?.sizeCode?.imageUrl} alt="box image" />
        <div className="flex flex-col gap-1 ">
          <div className="font-semibold text-2xl flex items-center gap-4 font-tektur">
            {box?.name}
            <div className="mb-2">{!isReservation && <LockStatusMarker lockStatus={lockStatus} isExternalBox={isExternalBox} />}</div>
          </div>

          <div className="flex flex-col gap-1">
            <Button theme="clear" fontSize="small" className="underline !w-fit" onClick={openMapModal}>
              {t('Show on map')}
            </Button>
            <div className="flex items-center text-primaryLight text-sm mt-2">
              <SquareIcon className="mr-2 text-accent w-[14px] h-[14px]" />
              {formatDecimal(box?.square)} {getSizeCodeTypeString(box?.sizeCode?.sizeCodeType)}
            </div>
            {!isReservation ? (
              <>
                {contractWithBookingInfo?.isContractPaid ? (
                  <div className="flex items-center text-primaryLight text-sm">
                    <BillIcon className="mr-2 text-accent w-[15px] h-[15px]" />
                    {t('Paid days')}: {contractWithBookingInfo.paidDays}
                  </div>
                ) : (
                  <div className="flex items-center text-error text-sm">
                    <BillIcon className="mr-2 text-error w-[15px] h-[15px]" />
                    {t('Paid days')}: 0
                  </div>
                )}
                {contractWithBookingInfo?.isContractPaid ? (
                  <div className="flex items-center text-primaryLight text-sm">
                    <AccessIcon className="mr-2 text-accent w-[15px] h-[15px]" />
                    {t('Access is allowed')}
                  </div>
                ) : (
                  <div className="flex items-center text-error text-sm">
                    <AccessIcon className="mr-2 text-error w-[15px] h-[15px]" />
                    {t('Access is denied')}
                  </div>
                )}
                {boxUsageRulesLink}
              </>
            ) : (
              <div className="flex flex-col">
                <div>
                  <span className="text-accent text-sm mr-1">{t('Start rent date')}:</span>
                  <span className="text-sm text-primaryLight">{getClientDateFormat(reservation?.startRentDate)}</span>
                </div>
                <div>
                  <span className="text-accent text-sm">{t('Min rental period')}:</span> <br />
                  <span className="text-sm text-primaryLight">{minRentalPeriodValue}</span>
                </div>
                {boxUsageRulesLink}
              </div>
            )}
          </div>
          {box?.floor && (
            <span className="text-primaryLight text-sm px-2 py-1 rounded-2xl border border-primaryLight w-fit mt-2">
              {getLocalizedString(box.floor.name)}
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 flex flex-col mt-2 justify-between gap-5">
        {reservation && isReservation ? (
          <div className="flex flex-col justify-between h-full">
            <div className="py-2">
              <div onClick={openReservationDetails} className="text-sm desktop:text-lg text-primaryLight font-tektur">
                {t('Reservation')}:
                <span className="pl-1 text-accent font-semibold cursor-pointer underline text-sm desktop:text-lg font-tektur">
                  #{reservation.reservationNumber}
                </span>
              </div>
              <div className="text-error font-semibold text-sm desktop:text-lg font-tektur">
                {t('Expires')}:{' '}
                <span className="text-black font-semibold text-sm desktop:text-lg font-tektur">
                  {getClientDateFormat(reservation.expirationDate)}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Button theme="outlined" onClick={openCancelReservationModal} containerClassName="w-full">
                {t('Cancel reservation')}
              </Button>
              {box?.unitType !== UnitType.OFFICE && (
                <Button onClick={openTakeUpReservationModal} containerClassName="w-full">
                  {t('Enter into contract')}
                </Button>
              )}
              {box?.unitType === UnitType.OFFICE && (
                <InfoTag className="w-full">{t('Administrator should contact you to provide instructions for rent')}</InfoTag>
              )}
            </div>
          </div>
        ) : (
          <div className="pt-2">
            <div className="text-primaryLight text-sm font-tektur desktop:text-lg">
              {t('Contract')}:
              <span
                className="pl-1 text-accent font-semibold cursor-pointer underline text-sm font-tektur desktop:text-lg"
                onClick={openContractDetails}
              >
                #{contractWithBookingInfo?.contractNumber}
              </span>
              {renderContractInvoicingInfo()}
            </div>
            <div className="flex gap-1.5 mt-4">
              <Button theme="secondary" onClick={openContractDetails} containerClassName="w-full">
                {t('Contract details')}
              </Button>
              {/* <OpenBox
                box={box}
                action={
                  <Button iconPosition="prev" containerClassName="w-full">
                    {t('Open')}
                  </Button>
                }
              /> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const content = isDesktop ? desktopVersion : mobileVersion;

  return (
    <>
      <div className="bg-secondaryLight rounded-xl">{content}</div>
      <Modal isOpen={isMapOpen} onClose={closeMapModal} width={900} centered>
        <div className="text-xl font-semibold font-tektur mb-4">{box?.name}</div>
        <div className="h-[600px]">
          {box?.warehouseId || warehouseId ? (
            <WarehouseMap
              selectedWarehouseId={warehouseId || box?.warehouseId}
              selectedBoxId={box?.originalEntityId}
              allowedSizeCodeId={box?.sizeCode?.sizeCodeId}
              preferredFloorId={box?.floor?.floorId}
              containerClassName="h-full"
              highlightOnlySelected
              centerOnSelected
              onSelectBox={() => {}}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-secondaryLight">{t('Map is not available')}</div>
          )}
        </div>
      </Modal>
    </>
  );
});
